import React, { useRef, useState, useEffect, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { FixedSizeList as List } from 'react-window'
import { replaceAccentedCharacters } from '../../helpers/utils'

export default function ReactMultipleFilter({
  title,
  optionsKeys,
  optionsDic,
  onChange,
  resetWatcher,
  isRequired,
  allSelectedOptions,
  disabled,
  selectAllRequired,
  sentRadio = false
}) {
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const [selectedOptions, setSelectedOptions] = useState(new Set())
  const [selectedFilterType, setSelectedFilterType] = useState(null)
  const [availableOptions, setAvailableOptions] = useState([])
  const [search, setSearch] = useState('')
  const [selectAll, setSelectAll] = useState(true)
  const filterContainer = useRef(null)
  const prevSelectedFilterTypeRef = useRef(null)

  const optionsDicMap = useMemo(
    () => new Map(optionsDic.map((o) => [o.key, o.value])),
    [optionsDic]
  )

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (filterContainer.current && !filterContainer.current.contains(e.target)) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  useEffect(() => {
    if (disabled) {
      setDropdownOpen(false)
    }
  }, [disabled])

  useEffect(() => {
    setSearch('')
    setSelectedFilterType(null)
    setAvailableOptions([])
    setSelectedOptions(new Set())
    prevSelectedFilterTypeRef.current = null
  }, [resetWatcher])

  useEffect(() => {
    if (selectedFilterType) {
      const selectedFilterOptions = optionsDicMap.get(selectedFilterType) || []
      setAvailableOptions(selectedFilterOptions)
      setSelectAll(false)
      setSearch('')
      // Only reset selected options when the filter type actually changed, not when optionsDic reference changed (e.g. parent re-render with inline array)
      if (prevSelectedFilterTypeRef.current !== selectedFilterType) {
        setSelectedOptions(new Set())
        prevSelectedFilterTypeRef.current = selectedFilterType
      }
    } else {
      prevSelectedFilterTypeRef.current = null
    }
  }, [selectedFilterType, optionsDicMap])

  useEffect(() => {
    const allSelected =
      availableOptions.length > 0 && availableOptions.every((option) => selectedOptions.has(option))
    setSelectAll(allSelected)
  }, [selectedOptions, availableOptions])

  const selectFilterType = (option) => {
    if (sentRadio && selectedFilterType === option) {
      setSelectedFilterType(null)
      setAvailableOptions([])
      setSelectedOptions(new Set())
      if (onChange) {
        const newAllSelectedOptions = new Map(allSelectedOptions)
        newAllSelectedOptions.delete(option)
        onChange(newAllSelectedOptions)
      }
      return
    }
    if (selectedFilterType !== option) {
      setSelectedFilterType(option)
      const selectedFilterOptions = optionsDicMap.get(option) || []
      setAvailableOptions(selectedFilterOptions)
      if (sentRadio && onChange) {
        const newAllSelectedOptions = new Map(allSelectedOptions)
        optionsKeys.forEach((k) => newAllSelectedOptions.delete(k))
        newAllSelectedOptions.set(option, [])
        onChange(newAllSelectedOptions)
      }
    }
  }

  const selectOption = (option) => {
    if (disabled) return
    const newSelected = new Set(selectedOptions)
    if (newSelected.has(option)) {
      newSelected.delete(option)
    } else {
      newSelected.add(option)
    }
    setSelectedOptions(newSelected)

    // Actualiza el estado global después de modificar la selección de una opción
    updateAllSelectedOptions(newSelected)
  }

  const handleFilterSearch = (e) => {
    if (disabled) return
    setSearch(e.target.value)
  }

  const filterOptions = () => {
    return availableOptions?.filter((option) => shouldDisplay(option))
  }
  const shouldDisplay = (option) => {
    if (search.length <= 0) return true
    return replaceAccentedCharacters(option).includes(replaceAccentedCharacters(search))
  }

  const filteredOptions = availableOptions.filter(
    (option) =>
      option != null &&
      replaceAccentedCharacters(option.toString()).includes(replaceAccentedCharacters(search))
  )

  const numOptionsToShow = filterOptions().length
  const OPTION_HEIGHT = 35
  const MAX_OPTIONS_VISIBLE = 8
  const containerMaxHeight = Math.min(numOptionsToShow, MAX_OPTIONS_VISIBLE) * OPTION_HEIGHT

  const shouldShowSelectAllButton = filterOptions().length <= 50 || selectAllRequired

  const toggleSelectAll = () => {
    const newSelected = new Set()
    if (!selectAll) {
      filterOptions().forEach((option) => newSelected.add(option))
    }
    setSelectedOptions(newSelected)
    setSelectAll(!selectAll)
    updateAllSelectedOptions(newSelected)
  }

  const updateAllSelectedOptions = (newSelected) => {
    if (selectedFilterType) {
      const newAllSelectedOptions = new Map(allSelectedOptions)
      if (newSelected.size > 0) {
        newAllSelectedOptions.set(selectedFilterType, Array.from(newSelected))
      } else if (sentRadio) {
        newAllSelectedOptions.set(selectedFilterType, [])
      } else {
        newAllSelectedOptions.delete(selectedFilterType)
      }
      onChange(newAllSelectedOptions)
    }
  }
  const RenderOption = ({ index, style }) => {
    const option = filteredOptions[index]
    const isOptionSelected = selectedOptions.has(option)
    return (
      <div style={style} className="react-filter-option" onClick={() => selectOption(option)}>
        <input type="checkbox" className="filter-checkbox" readOnly checked={isOptionSelected} />
        <div className="filter-checkbox-label">{option}</div>
      </div>
    )
  }

  return (
    <div className={`react-filter-box ${disabled ? 'react-filter-disabled' : ''}`}>
      <div
        title={title}
        onClick={() => !disabled && setDropdownOpen(!isDropdownOpen)}
        className="react-filter-box-clickable-zone"
      >
        <div className="react-filter-titles">
          <div className="react-filter-title-text">
            <div className="filter-title-overflow">{title.toUpperCase()}</div>
            {isRequired && selectedOptions.size == 0 && <span className="text-red">*</span>}
            {selectedOptions.size > 0 && (
              <span className="text-orange react-filter-selection-count">({selectedOptions.size})</span>
            )}
          </div>
          <FontAwesomeIcon
            className="react-filter-title-chevron"
            icon={isDropdownOpen ? faChevronUp : faChevronDown}
          />
        </div>
      </div>
      {isDropdownOpen && (
        <div className="react-multiple-filter-dropdown-box box-transition" ref={filterContainer}>
          <div className="react-filter-option-nohover">
            <div className="filter-subtitle">Filtrar por:</div>
          </div>
          {optionsKeys.map((option, idx) => {
            const isEmpty = (optionsDicMap.get(option) ?? []).length === 0
            return (
              <div
                className="react-filter-option-nohover"
                key={idx}
                {...(sentRadio && {
                  onClick: () => !isEmpty && selectFilterType(option)
                })}
              >
                <input
                  type="radio"
                  className="filter-radio"
                  id={`filterType-${idx}`}
                  name="filterType"
                  value={option}
                  checked={selectedFilterType === option}
                  onChange={() => selectFilterType(option)}
                  disabled={isEmpty}
                />
                <div className={isEmpty ? 'filter-radio-label-disabled' : 'filter-radio-label'}>
                  {option}
                </div>
              </div>
            )
          })}
          {selectedFilterType != null && availableOptions.length > 0 && (
            <>
              <hr className="filter-separation-line" />
              <div className="filter-search-input-container">
                <input
                  type="search"
                  className="filter-search-input"
                  placeholder="Palabras claves..."
                  value={search}
                  onChange={handleFilterSearch}
                  disabled={disabled}
                />
                {search && (
                  <div
                    title="Limpiar texto"
                    onClick={() => setSearch('')}
                    className="filter-search-input-x"
                  >
                    X
                  </div>
                )}
              </div>
              {!search && shouldShowSelectAllButton && (
                <div className="react-filter-option" onClick={toggleSelectAll}>
                  <input type="checkbox" className="filter-checkbox" readOnly checked={selectAll} />
                  <div className="filter-checkbox-label">
                    {selectAll ? 'Deseleccionar todos' : 'Seleccionar todos'}
                  </div>
                </div>
              )}

              {filterOptions().length > 0 ? (
                <List
                  height={300}
                  itemCount={filteredOptions.length}
                  itemSize={35}
                  width="100%"
                  itemData={availableOptions}
                >
                  {RenderOption}
                </List>
              ) : (
                search && (
                  <div className="react-filter-option">
                    <div className="filter-checkbox-label">No hubo resultados</div>
                  </div>
                )
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
